import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { Sparkles, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";

interface AIGeneratorModalProps {
  type: "email" | "pdf";
  category: string;
  onClose: () => void;
  onUse: (data: { subject?: string; content: string }) => void;
}

export default function AIGeneratorModal({
  type,
  category,
  onClose,
  onUse,
}: AIGeneratorModalProps) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState<"formal" | "friendly" | "excited">("friendly");
  const [generatedContent, setGeneratedContent] = useState<{
    subject?: string;
    content: string;
  } | null>(null);

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post<ApiResponse<{ subject?: string; content: string }>>(
        "/api/admin/templates/generate",
        { prompt, type, category, tone },
      ),
    onSuccess: (data) => {
      setGeneratedContent(data.data || null);
    },
    onError: (error: Error) => {
      toast.error(error.message || t("opsAdmin.templates.aiGenerateFailed"));
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
          <Sparkles className="text-primary-500 mr-2 inline h-6 w-6" />
          {t("opsAdmin.templates.aiTitle")}
        </h2>

        {!generatedContent ? (
          <div className="space-y-4">
            <div>
              <label className="label">{t("opsAdmin.templates.aiDescribe")}</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t("opsAdmin.templates.aiPromptPlaceholder")}
                className="input min-h-[100px]"
              />
            </div>

            <div>
              <label className="label">{t("opsAdmin.templates.aiTone")}</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value as typeof tone)}
                className="input"
              >
                <option value="formal">{t("opsAdmin.templates.toneFormal")}</option>
                <option value="friendly">{t("opsAdmin.templates.toneFriendly")}</option>
                <option value="excited">{t("opsAdmin.templates.toneExcited")}</option>
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn-ghost">
                {t("common.actions.cancel")}
              </button>
              <button
                onClick={() => generateMutation.mutate()}
                disabled={!prompt.trim() || generateMutation.isPending}
                className="btn-primary"
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Sparkles className="h-5 w-5" />
                )}
                {t("opsAdmin.templates.generate")}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {generatedContent.subject && (
              <div>
                <label className="label">{t("opsAdmin.templates.generatedSubject")}</label>
                <input
                  type="text"
                  value={generatedContent.subject}
                  readOnly
                  className="input bg-gray-50"
                />
              </div>
            )}

            <div>
              <label className="label">{t("opsAdmin.templates.generatedContent")}</label>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                {/* Content is sanitized with DOMPurify before rendering */}
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generatedContent.content) }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setGeneratedContent(null)} className="btn-ghost">
                {t("common.actions.retry")}
              </button>
              <button onClick={onClose} className="btn-ghost">
                {t("common.actions.cancel")}
              </button>
              <button onClick={() => onUse(generatedContent)} className="btn-primary">
                {t("opsAdmin.templates.use")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
